import React, { useEffect, useMemo, useState } from 'react';
import { PageShell } from '../../components/Ui';
import VaccinationChart from '../../components/emr/VaccinationChart';
import { useHmsRecords } from '../../hooks/useHmsRecords';
import { useLiveData } from '../../hooks/useLiveData';
import { ipApi } from '../../services/api';

/**
 * Hospital vaccination desk — patient chart + due/overdue register.
 * Chart data lives on the patient record; desk also logs administered doses as HMS records.
 */
export default function VaccinationPage() {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [records, setRecords] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const { data: patientsData, loading: patientsLoading, reload: reloadPatients } = useLiveData(
    () => ipApi.getPatients(search.trim() || undefined, 40),
    [search],
  );
  const patients = patientsData?.patients || [];
  const selected = useMemo(
    () => patients.find((p) => p.patientId === selectedId) || null,
    [patients, selectedId],
  );

  const { records: doseLogs, create: logDose, loading: logsLoading } = useHmsRecords('vaccination-record', 200);

  useEffect(() => {
    if (!selected) {
      setRecords([]);
      return;
    }
    setRecords(Array.isArray(selected.vaccinationRecords) ? selected.vaccinationRecords : []);
  }, [selected]);

  useEffect(() => {
    if (!selectedId && patients.length) setSelectedId(patients[0].patientId);
  }, [patients, selectedId]);

  const dueSoon = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const in30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
    return doseLogs.filter((d) => {
      const due = d.dueNext || '';
      return due && due >= today && due <= in30;
    }).length;
  }, [doseLogs]);

  const saveChart = async () => {
    if (!selected?.patientId) return;
    setBusy(true);
    setMsg('');
    try {
      await ipApi.patchPatient(selected.patientId, { vaccinationRecords: records });
      const newlyGiven = records.filter((r) => r.dateGiven && r.vaccine);
      for (const r of newlyGiven.slice(-5)) {
        const already = doseLogs.some(
          (d) => d.patientId === selected.patientId && d.vaccine === r.vaccine && d.dose === r.dose && d.dateGiven === r.dateGiven,
        );
        if (!already) {
          await logDose({
            patientId: selected.patientId,
            patientName: selected.name,
            uhid: selected.uhid || selected.patientId,
            vaccine: r.vaccine,
            dose: r.dose,
            dateGiven: r.dateGiven,
            batch: r.batch || '',
            dueNext: r.dueNext || '',
            status: 'Administered',
          });
        }
      }
      setMsg('Vaccination chart saved.');
      await reloadPatients();
    } catch (e) {
      setMsg(e.message || 'Could not save vaccination chart');
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell
      title="Vaccination Desk"
      subtitle="IAP-style immunization chart · Due doses · Batch tracking"
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Patients listed', val: patients.length, color: '#0d6e6a' },
          { label: 'Doses logged', val: doseLogs.length, color: '#0891b2' },
          { label: 'Due in 30 days', val: dueSoon, color: '#f59e0b' },
        ].map((s) => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px,280px) 1fr', gap: 14 }}>
        <div className="card" style={{ padding: 12 }}>
          <div className="field" style={{ marginBottom: 10 }}>
            <label>Find patient</label>
            <input
              className="input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name / phone / UHID"
            />
          </div>
          {patientsLoading && <p className="hint">Loading…</p>}
          {!patientsLoading && patients.length === 0 && <p className="hint">No patients found.</p>}
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {patients.map((p) => (
              <button
                key={p.patientId}
                type="button"
                onClick={() => setSelectedId(p.patientId)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', marginBottom: 6, padding: '10px 12px',
                  borderRadius: 8, border: `2px solid ${selectedId === p.patientId ? '#0d6e6a' : '#e2e8f0'}`,
                  background: selectedId === p.patientId ? '#f0fdfa' : '#fff', cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 13 }}>{p.name}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{p.uhid || p.patientId} · Age {p.age ?? '—'}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          {!selected && <p className="hint">Select a patient to open their vaccination chart.</p>}
          {selected && (
            <>
              <div className="info-banner mb">
                Chart for <strong>{selected.name}</strong> ({selected.uhid || selected.patientId}). Saves to patient record and logs administered doses.
              </div>
              {msg && <div className={`mb ${/could|fail|error/i.test(msg) ? 'error' : 'info-banner'}`}>{msg}</div>}
              <VaccinationChart records={records} onChange={setRecords} patientAge={selected.age} />
              <button type="button" className="btn btn-primary" style={{ marginTop: 12 }} disabled={busy} onClick={saveChart}>
                {busy ? 'Saving…' : 'Save vaccination chart'}
              </button>
            </>
          )}

          <div className="card" style={{ marginTop: 16 }}>
            <h3 style={{ marginTop: 0 }}>Recent administered doses</h3>
            {logsLoading && <p className="hint">Loading log…</p>}
            {!logsLoading && doseLogs.length === 0 && <p className="hint">No doses logged yet.</p>}
            <div className="table-wrap">
              <table className="data-table" style={{ fontSize: 12 }}>
                <thead>
                  <tr><th>Date</th><th>Patient</th><th>Vaccine</th><th>Dose</th><th>Batch</th><th>Due next</th></tr>
                </thead>
                <tbody>
                  {[...doseLogs]
                    .sort((a, b) => String(b.dateGiven || b.createdAt || '').localeCompare(String(a.dateGiven || a.createdAt || '')))
                    .slice(0, 40)
                    .map((d) => (
                      <tr key={d.recordId || d.id}>
                        <td>{d.dateGiven || '—'}</td>
                        <td>{d.patientName || d.uhid || '—'}</td>
                        <td style={{ fontWeight: 700 }}>{d.vaccine}</td>
                        <td>{d.dose}</td>
                        <td style={{ fontFamily: 'monospace' }}>{d.batch || '—'}</td>
                        <td>{d.dueNext || '—'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
